import { makeStyles } from '@material-ui/core';
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';
import React from 'react';

import { Colors } from 'src/common/ui/base';

/**
 * Error boundary component for My Day Today page sections.
 * This is used to isolate failures in individual sections of the My Day Today page,
 * preventing a single component's failure from crashing the entire page.
 */
export const SectionErrorBoundary: React.FC<{
  sectionName: string;
  children: React.ReactNode;
}> = ({ sectionName, children }) => {
  const styles = useStyles();

  return (
    <SentryErrorBoundary
      fallback={({ error }) => (
        <div className={styles.errorContainer} data-testid={`error-boundary-${sectionName}`}>
          <div className={styles.errorContent}>
            <h3>Error in {sectionName}</h3>
            <p>
              There was a problem loading this section. The rest of the page is still functional.
            </p>
            <details>
              <summary>Error details</summary>
              <p>{(error as Error)?.message || 'No details available.'}</p>
            </details>
          </div>
        </div>
      )}
    >
      {children}
    </SentryErrorBoundary>
  );
};

const useStyles = makeStyles({
  errorContainer: {
    border: `1px solid ${Colors.Red2}`,
    borderRadius: '4px',
    padding: '16px',
    margin: '8px 0',
    backgroundColor: Colors.Red1,
    minHeight: '100px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorContent: {
    maxWidth: '100%',
    '& h3': {
      margin: '0 0 8px 0',
      color: Colors.Red3,
    },
    '& p': {
      margin: '4px 0',
    },
    '& details': {
      marginTop: '8px',
      padding: '8px',
      backgroundColor: Colors.Ice,
      borderRadius: '4px',
    },
    '& summary': {
      cursor: 'pointer',
    },
  },
});
